// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Column,
  ExternalLinkIcon,
  MultiSelect,
  Row,
  Select,
  Switch,
  Text,
  Tooltip,
  TextWithTooltip,
} from "@hightouch/htui";
import pluralize from "pluralize";
import { useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { PermissionedCheckbox } from "src/components/permission";
import {
  type SourceColumn,
  SourceColumnList,
} from "src/pages/decision-engines/components/source-column-list";
import type {
  ColumnsResult,
  FMColumnConfig,
  FeatureMatrixFormValues,
  FilterableColumn,
} from "src/pages/decision-engines/configuration/feature-matrices/types";
import {
  ALL_AGGREGATION_OPTIONS,
  CAST_TO_OPTIONS,
  SANITIZE_PERMISSION,
  SHOULD_DIRTY_OPTION,
  OVERALL_COUNT_KEY,
  TIME_WINDOW_OPTIONS,
  buildUnusableModels,
  clearAggregations,
  findHiddenAggregationColumns,
  getAggregationOptions,
  getColumnsForModel,
  getModelTypeIcon,
  getRelationshipForModel,
  isTrulyMergedRelationship,
} from "src/pages/decision-engines/configuration/feature-matrices/utils";
import { createSchemaObjectLink } from "src/pages/schema/view/utils";
import { Link } from "src/router";
import { InlineFilterCell } from "./filter-components";
import { ImputationCell } from "./imputation-cell";
import { InsightsVariantsPopover } from "./insights-variants-popover";

type ColumnRow = FilterableColumn & SourceColumn;

export function ColumnTable({
  modelKey,
  columnsResult,
  consentPurgeEnabled,
  sourceId,
}: {
  modelKey: string;
  columnsResult: ColumnsResult;
  consentPurgeEnabled: boolean;
  sourceId: string | undefined;
}) {
  const { watch, setValue, formState, control } =
    useFormContext<FeatureMatrixFormValues>();
  const relationships = watch("relationships");
  const consentPurge = useWatch({ control, name: "consentPurge" });
  const timestampColumnOverrides = watch("timestampColumnOverrides");
  const enabledRelationships = watch("enabledRelationships");

  const isDisabled = modelKey !== "parent" && !enabledRelationships[modelKey];

  const rel = useMemo(
    () => getRelationshipForModel(modelKey, columnsResult),
    [modelKey, columnsResult],
  );
  const requiresAggregation = rel?.requiresAggregation ?? false;
  const isEventModel = rel?.isEventModel ?? false;

  // Null when not merged. Merged selections are removable but not editable,
  // preserving their generated feature names.
  const mergedIntoNames = useMemo(() => {
    if (!rel || !isTrulyMergedRelationship(rel, columnsResult.relationships)) {
      return null;
    }
    return (
      buildUnusableModels(columnsResult).find(
        (m) => m.relationshipId === rel.relationshipId,
      )?.mergedIntoNames ?? []
    );
  }, [rel, columnsResult]);
  const isMergedModel = mergedIntoNames !== null;
  const effectiveTimestamp = isEventModel
    ? timestampColumnOverrides[modelKey] || rel?.timestampColumn || ""
    : timestampColumnOverrides[modelKey] || "";
  const hasTimestamp = Boolean(effectiveTimestamp);

  const selectedColumns = relationships[modelKey] ?? {};

  const hiddenAggregatesByColumn = new Map(
    findHiddenAggregationColumns(relationships, columnsResult)
      .filter((column) => column.relationshipKey === modelKey)
      .map((column) => [column.columnName, column.aggregates]),
  );
  const hasHiddenAggregations = hiddenAggregatesByColumn.size > 0;
  const unaggregatedColumnCount = Object.keys(selectedColumns).filter(
    (name) => name !== OVERALL_COUNT_KEY && !hiddenAggregatesByColumn.has(name),
  ).length;

  // Supported sources must allow unflagging even after the revocations config is cleared.
  const showSanitizeColumn =
    consentPurgeEnabled &&
    (Boolean(consentPurge) ||
      Object.values(relationships).some((columnMap) =>
        Object.values(columnMap).some(
          (config) => config.sanitizeOnConsentRevocation,
        ),
      ));

  const allColumns = useMemo((): ColumnRow[] => {
    const modelColumns = getColumnsForModel(modelKey, columnsResult);
    const liveColumns = modelColumns.map((col) => ({
      ...col,
      uniqueKey: `${col.name}::${col.model_id}`,
      isStale: false,
    }));

    const liveNames = new Set(modelColumns.map((c) => c.name));
    const staleColumns = Object.keys(selectedColumns)
      .filter((name) => name !== OVERALL_COUNT_KEY && !liveNames.has(name))
      .map((name) => ({
        name,
        type: "unknown",
        model_id: "",
        model_name: "",
        alias: null as string | null,
        custom_type: null as string | null,
        top_k_error: null,
        top_k_enabled: false,
        last_top_k_sync_timestamp: null,
        top_k_sync_interval: null,
        description: null as string | null,
        disable: false,
        disable_preview: false,
        privacy_level: null,
        case_sensitive: null,
        metadata: null,
        column_reference: null,
        merged_relationship_names: null as string[] | null,
        uniqueKey: `${name}::stale`,
        isStale: true,
      }));

    return [...liveColumns, ...staleColumns].sort((a, b) => {
      if (a.isStale !== b.isStale) return a.isStale ? 1 : -1;
      const aMerged = (a.merged_relationship_names?.length ?? 0) > 0 ? 1 : 0;
      const bMerged = (b.merged_relationship_names?.length ?? 0) > 0 ? 1 : 0;
      return aMerged - bMerged;
    });
  }, [modelKey, columnsResult, selectedColumns]);

  const realSelectedKeys = Object.keys(selectedColumns).filter(
    (k) => k !== OVERALL_COUNT_KEY,
  );
  const selectedCount = realSelectedKeys.length;

  const modelName =
    modelKey === "parent"
      ? columnsResult.parentModel.modelName
      : (rel?.targetModelName ?? "");
  const modelId =
    modelKey === "parent"
      ? columnsResult.parentModel.modelId
      : rel?.targetModelId;
  const schemaModelHref =
    sourceId && modelId != null
      ? createSchemaObjectLink(sourceId, String(modelId), "columns")
      : null;

  const timestampColumns = useMemo(() => {
    if (!rel) return [];
    return rel.columns
      .filter(
        (c) =>
          c.type?.toLowerCase() === "timestamp" ||
          c.type?.toLowerCase() === "timestamp_with_timezone" ||
          c.type?.toLowerCase() === "date",
      )
      .map((c) => ({ label: c.name, value: c.name }));
  }, [rel]);

  const timestampColumnOverride = timestampColumnOverrides[modelKey] ?? "";
  const autoDetectedTimestampColumn = rel?.timestampColumn ?? "";

  const newColumnConfig = (): FMColumnConfig => ({
    aggregates: [],
    filters: [],
    includeUnfiltered: false,
    timeWindowDays: requiresAggregation ? null : [],
    dependency: false,
    castTo: null,
    imputation: null,
    insightsEnabled: false,
    insightsExcludedVariants: [],
    sanitizeOnConsentRevocation: false,
  });

  const activateAndSet = (newColumns: Record<string, FMColumnConfig>) => {
    setValue(`relationships.${modelKey}`, newColumns, SHOULD_DIRTY_OPTION);
    setValue(`enabledRelationships.${modelKey}`, true, SHOULD_DIRTY_OPTION);
  };

  const ensureEnabled = () => {
    if (modelKey !== "parent" && !enabledRelationships[modelKey]) {
      setValue(`enabledRelationships.${modelKey}`, true, SHOULD_DIRTY_OPTION);
    }
  };

  const toggleColumn = (columnName: string) => {
    // The activateAndSet branch below would wipe the other saved columns.
    if (isMergedModel && isDisabled) {
      return;
    }
    if (hasHiddenAggregations && !selectedColumns[columnName]) {
      return;
    }
    if (isDisabled) {
      activateAndSet({ [columnName]: newColumnConfig() });
      return;
    }
    if (selectedColumns[columnName]) {
      const updated = { ...selectedColumns };
      delete updated[columnName];
      setValue(`relationships.${modelKey}`, updated, SHOULD_DIRTY_OPTION);
    } else {
      ensureEnabled();
      setValue(
        `relationships.${modelKey}.${columnName}`,
        newColumnConfig(),
        SHOULD_DIRTY_OPTION,
      );
    }
  };

  const toggleOverallCount = () => {
    if (isMergedModel && (isDisabled || !selectedColumns[OVERALL_COUNT_KEY])) {
      return;
    }
    const overallCountConfig: FMColumnConfig = {
      aggregates: [],
      filters: [],
      includeUnfiltered: false,
      timeWindowDays: hasTimestamp ? [-1] : [],
      dependency: false,
      castTo: null,
      imputation: null,
      insightsEnabled: false,
      insightsExcludedVariants: [],
      sanitizeOnConsentRevocation: false,
    };
    if (isDisabled) {
      activateAndSet({ [OVERALL_COUNT_KEY]: overallCountConfig });
      return;
    }
    if (selectedColumns[OVERALL_COUNT_KEY]) {
      const updated = { ...selectedColumns };
      delete updated[OVERALL_COUNT_KEY];
      setValue(`relationships.${modelKey}`, updated, SHOULD_DIRTY_OPTION);
    } else {
      ensureEnabled();
      setValue(
        `relationships.${modelKey}.${OVERALL_COUNT_KEY}`,
        overallCountConfig,
        SHOULD_DIRTY_OPTION,
      );
    }
  };

  const defaultTimeWindows: number[] =
    selectedColumns[OVERALL_COUNT_KEY]?.timeWindowDays ?? [];

  const defaultTimeWindowLabel = useMemo(() => {
    if (defaultTimeWindows.length === 0) return "No default";
    return (
      "Default: " +
      defaultTimeWindows
        .map(
          (v) =>
            TIME_WINDOW_OPTIONS.find((o) => o.value === v)?.label ?? String(v),
        )
        .join(", ")
    );
  }, [defaultTimeWindows]);

  const isColumnSelected = (col: ColumnRow) =>
    !isDisabled && Boolean(selectedColumns[col.name]);

  const isRowLocked = (col: ColumnRow) =>
    !selectedColumns[col.name] || col.isStale || isMergedModel;

  const setColumnField = <K extends keyof FMColumnConfig>(
    col: ColumnRow,
    field: K,
    value: FMColumnConfig[K],
  ) => {
    const config = selectedColumns[col.name];
    if (!config) {
      return;
    }
    setValue(
      `relationships.${modelKey}.${col.name}`,
      { ...config, [field]: value },
      SHOULD_DIRTY_OPTION,
    );
  };

  const toggleAll = (visibleColumns: ColumnRow[], select: boolean) => {
    if (hasHiddenAggregations && (isDisabled || select)) {
      return;
    }
    if (isDisabled) {
      const all: Record<string, FMColumnConfig> = {};
      for (const col of visibleColumns) {
        all[col.name] = newColumnConfig();
      }
      activateAndSet(all);
      return;
    }
    if (!select) {
      const updated: Record<string, FMColumnConfig> = {};
      if (selectedColumns[OVERALL_COUNT_KEY]) {
        updated[OVERALL_COUNT_KEY] = selectedColumns[OVERALL_COUNT_KEY];
      }
      const visibleNames = new Set(visibleColumns.map((c) => c.name));
      for (const [name, config] of Object.entries(selectedColumns)) {
        if (name !== OVERALL_COUNT_KEY && !visibleNames.has(name)) {
          updated[name] = config;
        }
      }
      setValue(`relationships.${modelKey}`, updated, SHOULD_DIRTY_OPTION);
    } else {
      ensureEnabled();
      const all: Record<string, FMColumnConfig> = { ...selectedColumns };
      for (const col of visibleColumns) {
        all[col.name] = selectedColumns[col.name] ?? newColumnConfig();
      }
      setValue(`relationships.${modelKey}`, all, SHOULD_DIRTY_OPTION);
    }
  };

  const Icon = getModelTypeIcon(
    modelKey === "parent" ? "parent" : rel?.isEventModel ? "event" : "related",
  );

  return (
    <Column h="100%">
      <Column px={6} pt={3} pb={2} gap={2}>
        <Row justify="space-between" align="flex-start">
          <Row gap={2} align="center" maxWidth="100%" minWidth={0}>
            <Icon size="md" />
            <TextWithTooltip fontWeight="medium" size="lg">
              {modelName}
            </TextWithTooltip>
            {schemaModelHref && (
              <Tooltip message="Open model in schema in a new tab">
                <Link
                  href={schemaModelHref}
                  isExternal
                  aria-label="Open model in schema in a new tab"
                  display="inline-flex"
                  flexShrink={0}
                >
                  <ExternalLinkIcon aria-hidden />
                </Link>
              </Tooltip>
            )}
          </Row>
          <Row gap={4} align="center" flexShrink={0}>
            <Row gap={2} align="center">
              <Text color="text.secondary" size="sm">
                {pluralize("source column", selectedCount, true)} selected
              </Text>
            </Row>
            {modelKey !== "parent" && !isDisabled && !isMergedModel && (
              <Row align="center" gap={2}>
                <Controller
                  name={`legacyRelationships.${modelKey}`}
                  render={({ field }) => (
                    <Switch
                      size="sm"
                      isChecked={field.value ?? false}
                      onChange={field.onChange}
                      aria-label="Legacy naming"
                    />
                  )}
                />
                <Text size="sm" color="text.secondary">
                  Legacy naming
                </Text>
              </Row>
            )}
          </Row>
        </Row>
      </Column>

      {isMergedModel && (
        <Alert
          variant="inline"
          type="info"
          mx={6}
          mt={3}
          title={
            mergedIntoNames?.length
              ? `Columns merged into ${mergedIntoNames.join(", ")}`
              : "Columns merged into another model"
          }
          message="Selected columns keep their original feature names and can be removed but not edited. To add columns, select them under the merged-into model."
        />
      )}

      {hasHiddenAggregations && (
        <Alert
          variant="inline"
          type="warning"
          mx={6}
          mt={3}
          title="Aggregations from a 1:many relationship"
          message={`${pluralize("column", hiddenAggregatesByColumn.size, true)} still ${hiddenAggregatesByColumn.size === 1 ? "uses" : "use"} aggregations saved when this relationship was 1:many, so the agent gets aggregated features.${unaggregatedColumnCount > 0 ? ` ${pluralize("column", unaggregatedColumnCount, true)} without an aggregation ${unaggregatedColumnCount === 1 ? "isn't" : "aren't"} sent to the agent.` : ""} Remove the aggregations to add columns. This renames these features.`}
          actions={
            <Button
              size="sm"
              onClick={() => {
                setValue(
                  `relationships.${modelKey}`,
                  clearAggregations(selectedColumns),
                  SHOULD_DIRTY_OPTION,
                );
              }}
            >
              Remove aggregations
            </Button>
          }
        />
      )}

      {requiresAggregation && (
        <Row mx={6} mt={3} gap={3} align="stretch">
          <Box
            borderRadius="md"
            border="1px solid"
            borderColor="base.border"
            px={3}
            py={2}
            opacity={isDisabled ? 0.5 : 1}
            flex={1}
            minWidth={0}
          >
            <Row gap={3} align="center">
              <Checkbox
                isChecked={
                  !isDisabled && Boolean(selectedColumns[OVERALL_COUNT_KEY])
                }
                isDisabled={
                  isMergedModel &&
                  (isDisabled || !selectedColumns[OVERALL_COUNT_KEY])
                }
                onChange={toggleOverallCount}
                aria-label="Select overall count"
              />
              <Row gap={2} align="center" flex={1} minWidth={0}>
                <Text fontWeight="medium">Overall count</Text>
                <Badge size="sm">Computed</Badge>
              </Row>
              {!isDisabled &&
                !isMergedModel &&
                selectedColumns[OVERALL_COUNT_KEY] &&
                hasTimestamp && (
                  <Box width="220px">
                    <MultiSelect
                      options={TIME_WINDOW_OPTIONS}
                      value={defaultTimeWindows}
                      onChange={(value) => {
                        setValue(
                          `relationships.${modelKey}.${OVERALL_COUNT_KEY}.timeWindowDays`,
                          value,
                          SHOULD_DIRTY_OPTION,
                        );
                      }}
                      placeholder="Default: All time"
                    />
                  </Box>
                )}
            </Row>
          </Box>
          {!isDisabled && !isMergedModel && timestampColumns.length > 0 && (
            <Row borderRadius="md" px={3} py={2} gap={2} align="center">
              <Text size="sm" color="text.tertiary" whiteSpace="nowrap">
                Timestamp column
              </Text>
              <Box width="180px">
                <Select
                  isClearable
                  options={timestampColumns}
                  value={timestampColumnOverride}
                  onChange={(value) => {
                    if (value && value !== rel?.timestampColumn) {
                      setValue(
                        `timestampColumnOverrides.${modelKey}`,
                        value,
                        SHOULD_DIRTY_OPTION,
                      );
                    } else {
                      const overrides = { ...timestampColumnOverrides };
                      delete overrides[modelKey];
                      setValue(
                        "timestampColumnOverrides",
                        overrides,
                        SHOULD_DIRTY_OPTION,
                      );

                      if (!isEventModel) {
                        const updated = { ...selectedColumns };
                        for (const [colName, config] of Object.entries(
                          updated,
                        )) {
                          if (colName === OVERALL_COUNT_KEY) {
                            updated[colName] = {
                              ...config,
                              timeWindowDays: [],
                            };
                            continue;
                          }
                          updated[colName] = {
                            ...config,
                            aggregates: (config.aggregates ?? []).filter(
                              (a) => a !== "first" && a !== "last",
                            ),
                            timeWindowDays: null,
                          };
                        }
                        setValue(
                          `relationships.${modelKey}`,
                          updated,
                          SHOULD_DIRTY_OPTION,
                        );
                      }
                    }
                  }}
                  placeholder={
                    isEventModel && autoDetectedTimestampColumn
                      ? `Default: ${autoDetectedTimestampColumn}`
                      : "Select column"
                  }
                />
              </Box>
            </Row>
          )}
        </Row>
      )}

      <SourceColumnList
        columns={allColumns}
        isSelected={isColumnSelected}
        onToggle={(col) => toggleColumn(col.name)}
        onToggleAll={toggleAll}
        isColumnDisabled={(col) =>
          (isMergedModel || hasHiddenAggregations) &&
          (isDisabled || !selectedColumns[col.name])
        }
        isSelectAllDisabled={isMergedModel || hasHiddenAggregations}
        renderBadges={(col) => {
          const hiddenAggregates = hiddenAggregatesByColumn.get(col.name);
          return (
            <>
              {!col.isStale &&
                col.merged_relationship_names &&
                col.merged_relationship_names.length > 0 && (
                  <Tooltip message={col.model_name}>
                    <Badge size="sm">Merged</Badge>
                  </Tooltip>
                )}
              {hiddenAggregates && (
                <Tooltip
                  message={`Aggregated as ${hiddenAggregates
                    .map(
                      (agg) =>
                        ALL_AGGREGATION_OPTIONS.find((o) => o.value === agg)
                          ?.label ?? agg,
                    )
                    .join(", ")}`}
                >
                  <Badge size="sm" variant="warning">
                    Aggregated
                  </Badge>
                </Tooltip>
              )}
            </>
          );
        }}
        isDimmed={isDisabled}
        isInset
        extraColumns={[
          {
            name: "Cast to",
            min: "140px",
            cell: (col) => (
              <Box minWidth={0} width="100%">
                <Select
                  isDisabled={isRowLocked(col)}
                  isClearable
                  options={CAST_TO_OPTIONS}
                  value={selectedColumns[col.name]?.castTo ?? ""}
                  onChange={(value) => {
                    setColumnField(col, "castTo", value || null);
                  }}
                  placeholder="Original type"
                />
              </Box>
            ),
          },
          ...(requiresAggregation && !isDisabled
            ? [
                {
                  name: "Filter",
                  min: "180px",
                  cell: (col) => (
                    <InlineFilterCell
                      modelKey={modelKey}
                      columnName={col.name}
                      columns={allColumns}
                      isDisabled={isRowLocked(col)}
                    />
                  ),
                },
                ...(hasTimestamp
                  ? [
                      {
                        name: "Time windows",
                        min: "140px",
                        cell: (col) => {
                          const config = selectedColumns[col.name];
                          const isInheriting =
                            config && config.timeWindowDays === null;
                          return (
                            <Box minWidth={0} width="100%">
                              <MultiSelect
                                isDisabled={isRowLocked(col)}
                                isClearable
                                options={TIME_WINDOW_OPTIONS}
                                value={
                                  isInheriting
                                    ? []
                                    : (config?.timeWindowDays ?? [])
                                }
                                onChange={(value) => {
                                  setColumnField(
                                    col,
                                    "timeWindowDays",
                                    value.length === 0 ? null : value,
                                  );
                                }}
                                placeholder={
                                  isInheriting
                                    ? defaultTimeWindowLabel
                                    : "Time windows"
                                }
                              />
                            </Box>
                          );
                        },
                      },
                    ]
                  : []),
                {
                  name: "Aggregation",
                  min: "140px",
                  cell: (col) => {
                    const config = selectedColumns[col.name];
                    const options = getAggregationOptions(
                      col.type,
                      hasTimestamp,
                    );
                    const hasError = Boolean(
                      (formState.errors.relationships as any)?.[modelKey]?.[
                        col.name
                      ]?.aggregates,
                    );
                    return (
                      <Box minWidth={0} width="100%">
                        <MultiSelect
                          isDisabled={isRowLocked(col)}
                          isInvalid={hasError}
                          options={options}
                          value={config?.aggregates ?? []}
                          onChange={(value) => {
                            setColumnField(col, "aggregates", value);
                          }}
                          placeholder="Aggregations"
                        />
                      </Box>
                    );
                  },
                },
              ]
            : []),
          {
            name: "Dependency",
            min: "max-content",
            max: "max-content",
            header: () => (
              <Tooltip message="Include this column in the feature matrix without using it as a direct input to the model, so it remains available for downstream uses like insights.">
                <Text>Dependency</Text>
              </Tooltip>
            ),
            cell: (col) => (
              <Checkbox
                isChecked={selectedColumns[col.name]?.dependency ?? false}
                isDisabled={isRowLocked(col)}
                onChange={(e) => {
                  setColumnField(col, "dependency", e.target.checked);
                }}
                aria-label={`Mark ${col.name} as dependency`}
              />
            ),
          },
          {
            name: "Insights",
            min: "max-content",
            max: "max-content",
            header: () => (
              <Tooltip message="Include this column's features in insights breakdowns">
                <Text>Insights</Text>
              </Tooltip>
            ),
            cell: (col) => {
              const config = selectedColumns[col.name];
              const isInsightsEnabled = config?.insightsEnabled ?? false;
              const hasAggregates =
                requiresAggregation && config && config.aggregates.length > 0;
              return (
                <Row align="center" gap={2}>
                  <Checkbox
                    isChecked={isInsightsEnabled && Boolean(config)}
                    isDisabled={isRowLocked(col)}
                    onChange={(e) => {
                      setColumnField(col, "insightsEnabled", e.target.checked);
                    }}
                    aria-label={`Include ${col.name} in insights`}
                  />
                  {hasAggregates && isInsightsEnabled && !isMergedModel && (
                    <InsightsVariantsPopover
                      modelKey={modelKey}
                      columnName={col.name}
                      config={config}
                      requiresAggregation={requiresAggregation}
                    />
                  )}
                </Row>
              );
            },
          },
          ...(showSanitizeColumn
            ? [
                {
                  name: "Sanitize",
                  min: "max-content",
                  max: "max-content",
                  header: () => (
                    <Tooltip message="Clear this column's features for customers who revoke consent. Consent settings decide whether a run removes or only reports them.">
                      <Text>Sanitize</Text>
                    </Tooltip>
                  ),
                  cell: (col) => (
                    <PermissionedCheckbox
                      permission={SANITIZE_PERMISSION}
                      isChecked={
                        selectedColumns[col.name]
                          ?.sanitizeOnConsentRevocation ?? false
                      }
                      isDisabled={!selectedColumns[col.name] || col.isStale}
                      onChange={(e) => {
                        setColumnField(
                          col,
                          "sanitizeOnConsentRevocation",
                          e.target.checked,
                        );
                      }}
                      aria-label={`Sanitize ${col.name} on consent revocation`}
                    />
                  ),
                },
              ]
            : []),
          {
            name: "Null handling",
            header: () => (
              <Tooltip message="Null handling is automatically managed in the ML model based on the column type. Adding this will override the default behavior for the specified feature.">
                <Text>Null handling</Text>
              </Tooltip>
            ),
            min: "140px",
            cell: (col) => (
              <ImputationCell
                modelKey={modelKey}
                columnName={col.name}
                colType={col.type}
                isDisabled={isRowLocked(col)}
                requiresAggregation={requiresAggregation}
              />
            ),
          },
        ]}
      />
    </Column>
  );
}
